import React from 'react';
import styles from './UploadCard.module.css';

export interface UploadProgress {
  sent: number;
  total: number;
  key: string;
  projectId?: string;
  appId?: string;
  replaceVersionId?: string;
  paused?: boolean;
}

interface UploadCardProps {
  uploadId: string;
  progress: UploadProgress;
  isNewApp?: boolean;
  onPause: (uploadId: string) => void;
  onResume: (uploadId: string) => void;
  onCancel: (uploadId: string, key: string) => void;
}

export function UploadCard({
  uploadId,
  progress,
  isNewApp = false,
  onPause,
  onResume,
  onCancel,
}: UploadCardProps) {
  const percentage = Math.round((progress.sent / progress.total) * 100);
  const fileName = progress.key.split('/').pop() || 'Unknown file';
  const sizeMB = (progress.total / (1024 * 1024)).toFixed(2);
  const sentMB = (progress.sent / (1024 * 1024)).toFixed(2);
  const isReplacing = !!progress.replaceVersionId;

  const statusLabel = progress.paused
    ? 'Paused'
    : isReplacing
      ? `Replacing ${percentage}%`
      : isNewApp
        ? `Creating App ${percentage}%`
        : `Uploading ${percentage}%`;

  return (
    <div className={`${styles.card} ${isReplacing ? styles.replacing : ''}`}>
      <div className={styles.info}>
        <div className={styles.nameRow}>
          <span className={styles.fileName}>{fileName}</span>
          <span
            className={`${styles.badge} ${progress.paused ? styles.paused : ''} ${
              isReplacing ? styles.replacing : ''
            }`}
          >
            {statusLabel}
          </span>
        </div>
        <div className={styles.status}>
          {sentMB} MB / {sizeMB} MB
        </div>
        <div className={styles.progressBar}>
          <div
            className={`${styles.progressFill} ${percentage === 100 ? styles.complete : ''}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      <div className={styles.actions}>
        {percentage < 100 && (
          <>
            {progress.paused ? (
              <button onClick={() => onResume(uploadId)} className={styles.buttonPrimary}>
                Resume
              </button>
            ) : (
              <button onClick={() => onPause(uploadId)} className={styles.buttonSecondary}>
                Pause
              </button>
            )}
            <button onClick={() => onCancel(uploadId, progress.key)} className={styles.buttonDanger}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
